/**
 * The IIT Bombay × IRCC page's own API calls (Backend/ip_intel_programme_routes.py).
 *
 * Kept apart from ipintel/api.ts (whose call() is private) but on the same
 * base URL and visitor ids. Both endpoints answer {success, data} or
 * {success: false, error, field?, remaining?, closed_reason?}; 429 is
 * error "rate_limited".
 */

import { apiService } from '../../../services/apiService';
import { API_BASE, visitorIds } from './api';
import { IRCC_KEY } from './irccContent';

export type ClosedReason = '' | 'closed' | 'deadline' | 'full';

export interface IrccProgramme {
  key: string;
  label: string;
  partner_name: string;
  open: boolean;
  closed_reason: ClosedReason;
  /** ISO instant (the end of the closing day, India time) or null for "no date". */
  closes_at: string | null;
  closes_on_label?: string;
  /** false: unlimited places, so tech_cap, registered and remaining are null
   *  and the page shows no places count. Missing (an older server) = true. */
  places_limited?: boolean;
  tech_cap: number | null;
  registered: number | null;
  remaining: number | null;
  max_per_registration: number;
  currency: string;
  share_minor: number;
  full_price_minor: number;
  tax_label: string;
  share_label: string;
  share_amount_label: string;
  full_price_label: string;
  full_price_amount_label: string;
  /** The USD figure shown beside the full price (display only; ./usd.ts). */
  usd_price?: number;
  usd_note?: string;
  /** IRCC's address when set ('' otherwise); contact_email falls back to ours. */
  partner_email: string;
  contact_email: string;
  turnaround_days: number;
  /** Empty = any address; otherwise the address must be at one of these
   *  domains or a subdomain of one (ee.iitb.ac.in counts for iitb.ac.in). */
  allowed_email_domains: string[];
  /** true (the default): an address that has registered cannot send the
   *  form again (409 on `email`). false: it may; the cap (while places are
   *  limited) still applies. */
  one_per_address?: boolean;
}

export interface IrccTechInput { title: string; trl: string }

export interface IrccRegisterRequest {
  name: string;
  email: string;
  department: string;
  technologies: { title: string; trl: number }[];
  agree: boolean;
  /** Honeypot: always empty from a real visitor. */
  website: string;
  page_url?: string;
  visitor_id?: string;
  session_id?: string;
  user_id?: string;
}

export interface IrccRegisterResult {
  ref: string;
  count: number;
  share_total_label: string;
  email?: string;
  emailed?: boolean;
}

/** One shape for success and failure (the project's tsconfig does not narrow
 *  unions reliably): `data` only when ok; the rest only when not. */
export interface IrccResult<T> {
  ok: boolean;
  data: T;
  error: string;
  /** name | email | department | technologies | technologies.<i>.title|trl | agree */
  field?: string;
  remaining?: number;
  closedReason?: ClosedReason;
  status: number;
}

const bearer = (): Record<string, string> => {
  const t = apiService.getStoredAccessToken?.();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

async function send<T>(path: string, init?: RequestInit): Promise<IrccResult<T>> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE()}${path}`, {
      ...init,
      headers: { Accept: 'application/json', ...(init?.body ? { 'Content-Type': 'application/json' } : {}), ...(init?.headers || {}) },
      mode: 'cors',
      credentials: 'omit',
    });
  } catch {
    return { ok: false, data: undefined as unknown as T, error: 'Network error. Check your connection and try again.', status: 0 };
  }
  const b: any = await res.json().catch(() => ({}));
  if (res.ok && b?.success !== false && b?.data) {
    return { ok: true, data: b.data as T, error: '', status: res.status };
  }
  const d = b?.detail;
  const error = res.status === 429 || b?.error === 'rate_limited'
    ? 'rate_limited'
    : (typeof b?.error === 'string' && b.error)
      || (typeof d === 'string' ? d : Array.isArray(d) ? d.map((x: any) => x?.msg || '').filter(Boolean).join('; ') : '')
      || `Request failed (${res.status}).`;
  return {
    ok: false, data: undefined as unknown as T, error, status: res.status,
    field: typeof b?.field === 'string' ? b.field : undefined,
    remaining: typeof b?.remaining === 'number' ? b.remaining : undefined,
    closedReason: typeof b?.closed_reason === 'string' ? b.closed_reason as ClosedReason : undefined,
  };
}

export const irccApi = {
  getProgramme(key: string = IRCC_KEY) {
    return send<IrccProgramme>(`/api/v1/ip-intel/programme/${encodeURIComponent(key)}/config`);
  },
  /** A signed-in visitor's bearer goes along so the server can attach their user id. */
  register(body: Omit<IrccRegisterRequest, 'visitor_id' | 'session_id' | 'page_url'>, key: string = IRCC_KEY) {
    const full: IrccRegisterRequest = {
      ...body,
      page_url: (() => { try { return window.location.href.slice(0, 500); } catch { return undefined; } })(),
      ...visitorIds(),
    };
    return send<IrccRegisterResult>(`/api/v1/ip-intel/programme/${encodeURIComponent(key)}/register`, {
      method: 'POST', body: JSON.stringify(full), headers: body.user_id ? bearer() : {},
    });
  },
};
